/**
 * Integration landing-page data (Story 30.11) — the single source of truth for
 * the /integrations hub, each /integrations/<slug> page, and their FAQPage
 * JSON-LD. All 30 platforms are implemented adapters and available on every
 * plan (including free). Metrics are accurate per the integration-mapping
 * directive; keep them honest (don't list a metric a platform doesn't expose).
 */

export type IntegrationCategory =
  | "Google"
  | "Social Media"
  | "Advertising"
  | "SEO"
  | "Email & Commerce"
  | "Call Tracking"
  | "Reviews"
  | "CRM";

export interface IntegrationFaq {
  question: string;
  answer: string;
}

export interface Integration {
  slug: string;
  name: string;
  category: IntegrationCategory;
  /** One-line hero tagline. */
  tagline: string;
  /** Answer-first intro (~120–200 words), platform-specific. */
  intro: string;
  /** Representative metrics ProvenLeap reports for this platform. */
  keyMetrics: string[];
  faq: IntegrationFaq[];
}

const CTA = "All 30 integrations are included on every ProvenLeap plan, including the free Solo plan — no tier-gating.";

function faqCommon(name: string): IntegrationFaq[] {
  return [
    {
      question: `Is ${name} reporting included on the free plan?`,
      answer: `Yes. Every ProvenLeap plan — including the free Solo plan — includes all 30 integrations, so ${name} is available without upgrading. Paid plans raise the client limit and add white-label branding.`,
    },
    {
      question: `How do I connect ${name} to ProvenLeap?`,
      answer: `Add a client, open Integrations, choose ${name}, and complete the secure connection. ProvenLeap then syncs the client's data on a schedule and keeps the connection refreshed automatically.`,
    },
  ];
}

export const INTEGRATIONS: Integration[] = [
  // ── Google ─────────────────────────────────────────────
  {
    slug: "google-analytics-4",
    name: "Google Analytics 4",
    category: "Google",
    tagline: "Website traffic, engagement, and conversions in your client reports.",
    intro:
      "ProvenLeap connects to Google Analytics 4 (GA4) so agencies can report website traffic, user behavior, and conversions inside branded client reports and live dashboards. Connect a GA4 property once and ProvenLeap pulls sessions, users, engagement, and conversion data on a schedule — no manual exports, no rebuilding the same charts every month. Blend GA4 with ad platform data to show clients the full path from spend to on-site outcome.",
    keyMetrics: ["Sessions", "Active users", "Engaged sessions", "Conversions", "Engagement rate", "New users"],
    faq: faqCommon("Google Analytics 4"),
  },
  {
    slug: "google-ads",
    name: "Google Ads",
    category: "Google",
    tagline: "Spend, conversions, and ROAS across every campaign.",
    intro:
      "ProvenLeap connects to Google Ads so agencies can report ad spend, clicks, conversions, and ROAS in automated client reports. Pull performance by campaign, ad group, or keyword and break results down by any dimension. Because ProvenLeap blends Google Ads with GA4 and other platforms, clients see cost, conversions, and on-site outcomes side by side — the report writes itself on your schedule.",
    keyMetrics: ["Cost", "Clicks", "Impressions", "CTR", "Conversions", "Cost per conversion", "ROAS", "CPC"],
    faq: faqCommon("Google Ads"),
  },
  {
    slug: "google-search-console",
    name: "Google Search Console",
    category: "Google",
    tagline: "Organic search performance and keyword rankings.",
    intro:
      "ProvenLeap connects to Google Search Console so agencies can report organic search performance — clicks, impressions, average position, and click-through rate — in client reports and dashboards. Track which queries and pages drive organic traffic, and pair Search Console with GA4 to tie rankings to on-site conversions. Data syncs automatically so SEO reporting stays current without manual work.",
    keyMetrics: ["Clicks", "Impressions", "Average position", "CTR"],
    faq: faqCommon("Google Search Console"),
  },
  {
    slug: "google-business-profile",
    name: "Google Business Profile",
    category: "Google",
    tagline: "Local visibility, actions, and search impressions.",
    intro:
      "ProvenLeap connects to Google Business Profile so agencies with local-services clients can report local visibility, customer actions, and search impressions. Show clients how many people found their business, requested directions, called, or visited the website — all in a branded report. Combine with Google Ads and Search Console for a complete local-marketing picture.",
    keyMetrics: ["Search impressions", "Direction requests", "Call clicks", "Website clicks", "Bookings"],
    faq: faqCommon("Google Business Profile"),
  },
  {
    slug: "youtube",
    name: "YouTube Analytics",
    category: "Google",
    tagline: "Video performance, watch time, and channel growth.",
    intro:
      "ProvenLeap connects to YouTube Analytics so agencies can report video and channel performance for clients — views, watch time, average view duration, and subscriber growth. Report at the channel or video level and blend YouTube with other platforms to show how video content contributes to the wider marketing mix. Reporting is automated on your schedule.",
    keyMetrics: ["Views", "Watch time", "Average view duration", "Subscribers gained", "Likes", "Comments"],
    faq: faqCommon("YouTube Analytics"),
  },
  {
    slug: "google-pagespeed",
    name: "Google PageSpeed",
    category: "Google",
    tagline: "Core Web Vitals and performance scores.",
    intro:
      "ProvenLeap connects to Google PageSpeed Insights so agencies can report site performance and Core Web Vitals in client reports. Show performance scores and field metrics like LCP, CLS, and INP to make the case for site-speed work. PageSpeed is a point-in-time analysis of any URL — ProvenLeap surfaces it alongside your SEO and analytics data.",
    keyMetrics: ["Performance score", "LCP", "CLS", "INP", "FCP", "TTFB"],
    faq: faqCommon("Google PageSpeed"),
  },
  // ── Social Media ───────────────────────────────────────
  {
    slug: "facebook-pages",
    name: "Facebook Pages",
    category: "Social Media",
    tagline: "Page reach, engagement, and follower growth.",
    intro:
      "ProvenLeap connects to Facebook Pages so agencies can report organic page performance — reach, engagement, follower growth, and post performance — in branded client reports. Show which posts resonated and how the audience grew, and pair Pages with Meta Ads to separate organic from paid results. Data syncs automatically.",
    keyMetrics: ["Page reach", "Engagement", "Follower growth", "Post reach", "Post engagement"],
    faq: faqCommon("Facebook Pages"),
  },
  {
    slug: "facebook-ads",
    name: "Facebook & Meta Ads",
    category: "Social Media",
    tagline: "Ad spend, conversions, and attribution across Meta.",
    intro:
      "ProvenLeap connects to Facebook and Meta Ads so agencies can report ad spend, conversions, and ROAS across Facebook and Instagram campaigns. Break results down by campaign, ad set, and creative, and report action breakdowns and attribution windows. Blend Meta Ads with GA4 and Google Ads so clients see every channel's contribution in one report.",
    keyMetrics: ["Spend", "Impressions", "Clicks", "CTR", "CPC", "Conversions", "ROAS", "Cost per result"],
    faq: faqCommon("Facebook & Meta Ads"),
  },
  {
    slug: "instagram",
    name: "Instagram Business",
    category: "Social Media",
    tagline: "Reach, engagement, and audience growth on Instagram.",
    intro:
      "ProvenLeap connects to Instagram Business so agencies can report organic Instagram performance — reach, engagement, and follower growth — in client reports and dashboards. Show top posts and audience trends, and combine Instagram with Meta Ads to separate organic reach from paid. Reporting is automated on your schedule.",
    keyMetrics: ["Reach", "Engagement", "Follower growth", "Profile views", "Likes", "Comments", "Saves"],
    faq: faqCommon("Instagram Business"),
  },
  {
    slug: "instagram-ads",
    name: "Instagram Ads",
    category: "Social Media",
    tagline: "Instagram ad performance within your Meta reporting.",
    intro:
      "ProvenLeap reports Instagram Ads performance as part of Meta Ads — filtered to Instagram placements — so agencies can show spend, conversions, and ROAS for Instagram campaigns specifically. Break results down by campaign, ad set, and creative, and blend with organic Instagram and other channels for the full picture.",
    keyMetrics: ["Spend", "Impressions", "Clicks", "CTR", "Conversions", "ROAS", "Cost per result"],
    faq: faqCommon("Instagram Ads"),
  },
  {
    slug: "linkedin-pages",
    name: "LinkedIn Pages",
    category: "Social Media",
    tagline: "Company-page reach, engagement, and follower growth.",
    intro:
      "ProvenLeap connects to LinkedIn Pages so B2B agencies can report company-page performance — impressions, engagement, and follower growth — in client reports. Show how organic LinkedIn content performs and how the follower base grows over time, and pair with LinkedIn Ads to separate organic from paid.",
    keyMetrics: ["Impressions", "Engagement", "Follower growth", "Clicks", "Reactions", "Shares"],
    faq: faqCommon("LinkedIn Pages"),
  },
  {
    slug: "linkedin-ads",
    name: "LinkedIn Ads",
    category: "Social Media",
    tagline: "B2B ad spend, leads, and campaign performance.",
    intro:
      "ProvenLeap connects to LinkedIn Ads so B2B agencies can report ad spend, leads, and campaign performance in branded client reports. Track impressions, clicks, conversions, and cost per lead across LinkedIn campaigns, and blend with GA4 and CRM data to show the full lead journey. Reporting is automated on your schedule.",
    keyMetrics: ["Spend", "Impressions", "Clicks", "CTR", "CPC", "Conversions", "Cost per lead"],
    faq: faqCommon("LinkedIn Ads"),
  },
  {
    slug: "tiktok",
    name: "TikTok Business",
    category: "Social Media",
    tagline: "TikTok ads and organic performance in one report.",
    intro:
      "ProvenLeap connects to TikTok Business so agencies can report both TikTok ad performance and organic content results. Track spend, impressions, clicks, conversions, and video engagement, and show how TikTok contributes to the wider marketing mix. Data syncs automatically for on-schedule reporting.",
    keyMetrics: ["Spend", "Impressions", "Clicks", "CTR", "Conversions", "Video views", "Engagement"],
    faq: faqCommon("TikTok Business"),
  },
  // ── Advertising ────────────────────────────────────────
  {
    slug: "microsoft-ads",
    name: "Microsoft Ads",
    category: "Advertising",
    tagline: "Bing and Microsoft Audience Network reporting.",
    intro:
      "ProvenLeap connects to Microsoft Ads so agencies can report search and audience-network performance across Bing and Microsoft's network. Track spend, clicks, conversions, quality score, and impression share, and blend with Google Ads to compare search channels in one report. Reporting is automated on your schedule.",
    keyMetrics: ["Spend", "Clicks", "Impressions", "CTR", "Conversions", "Quality score", "Impression share"],
    faq: faqCommon("Microsoft Ads"),
  },
  {
    slug: "pinterest-ads",
    name: "Pinterest Ads",
    category: "Advertising",
    tagline: "Paid and earned Pinterest performance.",
    intro:
      "ProvenLeap connects to Pinterest Ads so agencies can report paid campaign performance plus earned metrics — the organic amplification of paid pins. Track spend, impressions, clicks, conversions, and configurable attribution, and show clients the full Pinterest picture in a branded report.",
    keyMetrics: ["Spend", "Impressions", "Clicks", "CTR", "Conversions", "Earned impressions"],
    faq: faqCommon("Pinterest Ads"),
  },
  {
    slug: "snapchat-ads",
    name: "Snapchat Ads",
    category: "Advertising",
    tagline: "Swipe-ups, engaged views, and video performance.",
    intro:
      "ProvenLeap connects to Snapchat Ads so agencies can report campaign performance including swipe-ups, engaged views, and video quartile completions. Track spend, impressions, and conversions, and blend Snapchat with other paid social channels for a complete report. Reporting is automated on your schedule.",
    keyMetrics: ["Spend", "Impressions", "Swipe-ups", "Engaged views", "Conversions", "Video views"],
    faq: faqCommon("Snapchat Ads"),
  },
  {
    slug: "reddit-ads",
    name: "Reddit Ads",
    category: "Advertising",
    tagline: "Community-targeted campaign performance.",
    intro:
      "ProvenLeap connects to Reddit Ads so agencies can report campaign performance across communities, with click and view-through attribution. Track spend, impressions, clicks, and conversions, and use subreddit-level dimensions to show where results come from. Data syncs automatically for on-schedule client reporting.",
    keyMetrics: ["Spend", "Impressions", "Clicks", "CTR", "Conversions", "CPC"],
    faq: faqCommon("Reddit Ads"),
  },
  {
    slug: "amazon-ads",
    name: "Amazon Ads",
    category: "Advertising",
    tagline: "Sponsored Products, Brands, Display, and ACOS/ROAS.",
    intro:
      "ProvenLeap connects to Amazon Ads so agencies with e-commerce clients can report Sponsored Products, Brands, and Display performance — including ACOS, ROAS, and new-to-brand metrics. Track spend, sales, and efficiency across ad types, and blend Amazon with the client's other channels in a branded report.",
    keyMetrics: ["Spend", "Sales", "ACOS", "ROAS", "Clicks", "Impressions", "New-to-brand"],
    faq: faqCommon("Amazon Ads"),
  },
  // ── SEO ────────────────────────────────────────────────
  {
    slug: "ahrefs",
    name: "Ahrefs",
    category: "SEO",
    tagline: "Domain Rating, backlinks, and organic keywords.",
    intro:
      "ProvenLeap connects to Ahrefs so agencies can report SEO authority and backlink metrics in client reports — Domain Rating, referring domains, backlinks, and organic keyword data. Show link-building progress and organic visibility, and pair Ahrefs with Search Console and GA4 for a full SEO story. Reporting is automated on your schedule.",
    keyMetrics: ["Domain Rating", "Referring domains", "Backlinks", "Organic keywords", "Organic traffic"],
    faq: faqCommon("Ahrefs"),
  },
  {
    slug: "semrush",
    name: "Semrush",
    category: "SEO",
    tagline: "Authority Score, rankings, and visibility.",
    intro:
      "ProvenLeap connects to Semrush so agencies can report SEO metrics including Authority Score, keyword rankings, and organic visibility in client reports. Track ranking movements and competitive visibility, and blend Semrush with Search Console and analytics for a complete SEO report. Data syncs automatically.",
    keyMetrics: ["Authority Score", "Organic keywords", "Organic traffic", "Backlinks", "Keyword rankings"],
    faq: faqCommon("Semrush"),
  },
  {
    slug: "moz",
    name: "Moz",
    category: "SEO",
    tagline: "Domain Authority, Page Authority, and Spam Score.",
    intro:
      "ProvenLeap connects to Moz so agencies can report Domain Authority, Page Authority, and Spam Score in client reports. Show authority progress over time and link metrics, and pair Moz with Search Console and analytics for a rounded SEO report. Reporting is automated on your schedule.",
    keyMetrics: ["Domain Authority", "Page Authority", "Spam Score", "Linking root domains", "Backlinks"],
    faq: faqCommon("Moz"),
  },
  // ── Email & Commerce ───────────────────────────────────
  {
    slug: "mailchimp",
    name: "Mailchimp",
    category: "Email & Commerce",
    tagline: "Campaign opens, clicks, and e-commerce revenue.",
    intro:
      "ProvenLeap connects to Mailchimp so agencies can report email campaign and automation performance — opens, clicks, bounces, and e-commerce revenue — in branded client reports. Track audience growth and campaign results, and blend email with the client's other channels to show email's contribution to revenue.",
    keyMetrics: ["Open rate", "Click rate", "Bounce rate", "Unsubscribe rate", "Revenue", "Subscribers"],
    faq: faqCommon("Mailchimp"),
  },
  {
    slug: "klaviyo",
    name: "Klaviyo",
    category: "Email & Commerce",
    tagline: "Email, SMS, and revenue attribution for e-commerce.",
    intro:
      "ProvenLeap connects to Klaviyo so e-commerce agencies can report email, SMS, and flow performance with revenue attribution. Track campaign and flow results — opens, clicks, and attributed revenue — and show clients how owned channels drive sales. Blend Klaviyo with Shopify and ad platforms for a full commerce report.",
    keyMetrics: ["Open rate", "Click rate", "Attributed revenue", "Placed orders", "Unsubscribe rate"],
    faq: faqCommon("Klaviyo"),
  },
  {
    slug: "shopify",
    name: "Shopify",
    category: "Email & Commerce",
    tagline: "Sales, orders, AOV, and store performance.",
    intro:
      "ProvenLeap connects to Shopify so e-commerce agencies can report store performance — sales, orders, average order value, and sessions — in branded client reports. Show revenue trends and conversion, and blend Shopify with ad platforms and email so clients see the full path from spend to sale.",
    keyMetrics: ["Total sales", "Orders", "Average order value", "Sessions", "Conversion rate", "Revenue per session"],
    faq: faqCommon("Shopify"),
  },
  // ── Call Tracking ──────────────────────────────────────
  {
    slug: "callrail",
    name: "CallRail",
    category: "Call Tracking",
    tagline: "Call volume, sources, and conversation insights.",
    intro:
      "ProvenLeap connects to CallRail so agencies with lead-gen clients can report call tracking data — total calls, first-time callers, missed calls, and call sources — in branded reports. Show which campaigns and channels drive phone leads, and blend call data with ad spend to prove ROI on lead generation.",
    keyMetrics: ["Total calls", "Answered calls", "Missed calls", "First-time callers", "Average duration"],
    faq: faqCommon("CallRail"),
  },
  {
    slug: "calltrackingmetrics",
    name: "CallTrackingMetrics",
    category: "Call Tracking",
    tagline: "Omnichannel calls, SMS, forms, and chat.",
    intro:
      "ProvenLeap connects to CallTrackingMetrics so agencies can report omnichannel lead tracking — calls, SMS, forms, and chat — in client reports. Track lead volume and sources across channels and show which campaigns drive contacts. Blend with ad platforms to attribute leads to spend.",
    keyMetrics: ["Total calls", "Answered calls", "Missed calls", "Text messages", "Form submissions"],
    faq: faqCommon("CallTrackingMetrics"),
  },
  // ── Reviews ────────────────────────────────────────────
  {
    slug: "google-reviews",
    name: "Google Reviews",
    category: "Reviews",
    tagline: "Star ratings, review volume, and reply rate.",
    intro:
      "ProvenLeap connects to Google Reviews (via Business Profile) so agencies can report review performance — average rating, review count, and reply rate — in client reports. Show reputation trends and how quickly reviews get responses, and pair with local metrics for a complete local-marketing report.",
    keyMetrics: ["Average rating", "Review count", "New reviews", "Reply rate", "Response time"],
    faq: faqCommon("Google Reviews"),
  },
  {
    slug: "trustpilot",
    name: "Trustpilot",
    category: "Reviews",
    tagline: "TrustScore, review volume, and reply tracking.",
    intro:
      "ProvenLeap connects to Trustpilot so agencies can report TrustScore, review volume, and reply performance in client reports. Track service and product reviews, invited vs organic, and reply rate over time. Blend Trustpilot with Google Reviews for a cross-platform reputation report.",
    keyMetrics: ["TrustScore", "Review count", "New reviews", "Reply rate", "Average response time"],
    faq: faqCommon("Trustpilot"),
  },
  {
    slug: "yelp",
    name: "Yelp",
    category: "Reviews",
    tagline: "Yelp ratings and review counts.",
    intro:
      "ProvenLeap connects to Yelp so agencies with local clients can report Yelp ratings and review counts in client reports. Track reputation on Yelp alongside Google and Trustpilot for a complete review picture. Data syncs automatically for on-schedule reporting.",
    keyMetrics: ["Rating", "Review count", "New reviews"],
    faq: faqCommon("Yelp"),
  },
  // ── CRM ────────────────────────────────────────────────
  {
    slug: "hubspot",
    name: "HubSpot",
    category: "CRM",
    tagline: "Full-funnel traffic, leads, and attribution.",
    intro:
      "ProvenLeap connects to HubSpot so agencies can report full-funnel performance — traffic, leads, MQLs, SQLs, and customers — with multi-touch attribution. Show clients how marketing turns visitors into pipeline, and blend HubSpot with ad platforms and GA4 for a complete acquisition report. Reporting is automated on your schedule.",
    keyMetrics: ["Sessions", "New contacts", "MQLs", "SQLs", "Customers", "Attribution"],
    faq: faqCommon("HubSpot"),
  },
];

export const TOTAL_INTEGRATION_PAGES = INTEGRATIONS.length;

/** Category display order for the hub. */
export const CATEGORY_ORDER: IntegrationCategory[] = [
  "Google",
  "Social Media",
  "Advertising",
  "SEO",
  "Email & Commerce",
  "Call Tracking",
  "Reviews",
  "CRM",
];

export function getIntegrationBySlug(slug: string): Integration | undefined {
  return INTEGRATIONS.find((i) => i.slug === slug);
}

export function integrationsByCategory(): { category: IntegrationCategory; items: Integration[] }[] {
  return CATEGORY_ORDER.map((category) => ({
    category,
    items: INTEGRATIONS.filter((i) => i.category === category),
  })).filter((g) => g.items.length > 0);
}

export { CTA as INTEGRATIONS_CTA };
